import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSelector } from 'react-redux';
import { selectIsTeamSpace } from '@/store/selectors/spaceSelectors';
import i18n from '@/i18n/config';
import PreloadLink from '@/components/common/PreloadLink';
import ExpertSkillsNavIcon from '@/components/common/icons/ExpertSkillsNavIcon';
import { useRoutePreloader } from '@/hooks/useRoutePreloader';
import { getUserInfo, setUserInfo as saveUserInfo } from '@/utils/auth';
import { eventBus } from '@/utils/eventBus';
import authService from '@/services/amai/auth.service';
import { AGENT_BASE_URL } from '@/config/agent';
import { useBrand } from '@/contexts/BrandContext';
import AccountMenu from '@/components/business/AccountMenu';

// 定义路由状态类型
interface RouteState {
  showSidebar: boolean;
}

// 导入自定义SVG图标
import NewHomeIcon from '@/assets/icons/home-icon-new.svg?url';
import ActiveHomeIcon from '@/assets/icons/home-icon-active.svg?url';
import ArrowIcon from '@/assets/icons/sidebar-arrow.svg?url';
import InactiveTextIcon from '@/assets/icons/text-icon-inactive.svg?url';
import ActiveTextIcon from '@/assets/icons/text-icon-active.svg?url';
import InactiveAssetsIcon from '@/assets/icons/assets-icon-inactive.svg?url';
import ActiveAssetsIcon from '@/assets/icons/assets-icon-active.svg?url';
import InactiveImageIcon from '@/assets/icons/image-icon-inactive.svg?url';
import ActiveImageIcon from '@/assets/icons/image-icon-active.svg?url';
import InactiveVideoIcon from '@/assets/icons/video-icon-inactive.svg?url';
import ActiveVideoIcon from '@/assets/icons/video-icon-active.svg?url';
import InactiveToolsIcon from '@/assets/icons/tools-icon-inactive.svg?url';
import ActiveToolsIcon from '@/assets/icons/tools-icon-active.svg?url';
import AssetsIconRicegrainvalue from '@/assets/icons/assets-icon-ricegrainvalue.svg?url';
import MemberIcon from '@/assets/icons/member.svg?url';
import InviteIcon from '@/assets/icons/invite-icon.png';
import VideoEdit from '@/assets/icons/video-edit-icon.svg?url';
import VideoEditActive from '@/assets/icons/video-edit-active-icon.svg?url';
import AiAgent from '@/assets/icons/ai.svg?url';


const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  // 团队空间不展示「邀请好友」入口（邀请奖励属个人账户体系，20261103）
  const isTeamSpace = useSelector(selectIsTeamSpace);
  // 强制重渲染：RouterProvider 内部不响应外层 Context 变化，需要手动触发
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const handler = () => forceUpdate(n => n + 1);
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, []);
  const { preloadRouteWithDelay, cancelPreload, smartPreload } = useRoutePreloader();
  const [creationOpen, setCreationOpen] = useState(true);
  const [userInfo, setUserInfo] = useState<{
    user_id: string;
    user_name: string;
    user_avatar: string;
    mobile?: string;
    auth_identifier?: string;
  } | null>(() => getUserInfo());
  const { hiddenSidebarItems = [], hiddenSidebarSections = [], icpRecord } = useBrand();

  // 三击首页图标显示版本号
  const homeClickCount = useRef(0);
  const homeClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHomeClick = () => {
    homeClickCount.current += 1;
    if (homeClickTimer.current) {
      clearTimeout(homeClickTimer.current);
    }
    
    // 500ms 内重置点击计数
    homeClickTimer.current = setTimeout(() => {
      if (homeClickCount.current >= 3) {
        eventBus.emit('showVersion');
      }
      homeClickCount.current = 0;
    }, 500);
  };

  const toggleCreation = () => {
    setCreationOpen(!creationOpen);
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await authService.getUserProfile();
        if (response.code === 0) {
          saveUserInfo(response.data);
          setUserInfo(response.data);
          if (response.data.show_new_user_gift) {
            eventBus.emit('showWelcome');
          }
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
      }
    };

    fetchUserProfile();
    smartPreload(location.pathname);

    const handleUserInfoUpdate = (updatedUserInfo: any) => {
      setUserInfo(updatedUserInfo);
    };

    eventBus.on('userInfoUpdated', handleUserInfoUpdate);

    return () => {
      eventBus.off('userInfoUpdated', handleUserInfoUpdate);
    };
  }, [smartPreload, location.pathname]);

  // 定义用于处理图片和视频创作的直接导航
  const handleCreationClick = (path: string) => {
    navigate(path);
  };

  // 定义用于跳转直接导航
  const handleClickJump = () => {
    const token = localStorage.getItem('anmo_ai_token');
    const url = new URL(`${AGENT_BASE_URL}/global?key=space`);
    const lang = i18n.language.startsWith('en') ? 'en' : 'zh';
    url.searchParams.set('lang', lang);
    if (token) {
      url.searchParams.set('token', token);
      window.open(url.toString(), '_blank');
    }
  };
  // 保持引用避免 TS6133（基线已存在的未使用告警，原有 JSX 引用已随账户区迁出）
  void handleClickJump;

  // 处理鼠标悬停预加载
  const handleMouseEnter = (path: string) => {
    preloadRouteWithDelay(path, 150); // 更短的延迟，因为是直接点击的元素
  };

  const handleMouseLeave = () => {
    cancelPreload();
  };

  const activeColor = '#FF6A39';
  const borderColor = 'rgba(255,255,255,0.06)';

  const isPathActive = (path: string) => {
    if (path === '/home') {
      return location.pathname === path;
    }
    // 精确匹配会员和米粒值页面
    if (path === '/ricegrain/membership' || path === '/ricegrain/purchase') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isCreationActive = location.pathname.startsWith('/home/creation');

  return (
    <div
      className="sidebar-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(calc(var(--vh, 1vh) * 100) - 66px)', // 使用视口高度减去Logo高度
        background: '#101010',
        position: 'relative', // 恢复相对定位，让父容器的固定定位生效
        borderTop: 'none', // 移除顶部边框，由MainLayout控制
        zIndex:200
      }}
    >
      {/* 菜单内容区域 - 使用 flex: 1 占据剩余空间 */}
      <div
        style={{
          flex: 1,
          padding: '8px 16px',
          paddingBottom: '8px',
          overflowY: 'auto', // 允许垂直滚动
          // 隐藏滚动条但保留滚动功能
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
        }}
        className="sidebar-menu-content"
      >
        {/* 首页（品牌可经 hiddenSidebarItems 隐藏，如 gxzs 站点默认落地任务大厅） */}
        {!hiddenSidebarItems.includes('home') && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            
          >
            <img
              src={isPathActive('/home') ? ActiveHomeIcon : NewHomeIcon}
              alt={t('sidebar.home')}
              onClick={handleHomeClick}

              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer',
              }}
            />
          </div>

          <PreloadLink
            to="/home"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/home') ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
              padding: '12px 16px 12px 50px', // 增加左侧内边距给图标空间
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => {
              if (!isPathActive('/home')) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }
            }}
            onMouseLeave={e => {
              if (!isPathActive('/home')) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <span
              style={{
                color: isPathActive('/home') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/home') ? '600' : '400',
                lineHeight: '16px',
              }}
            >
              {t('sidebar.home')}
            </span>
          </PreloadLink>
        </div>
        )}

        {/* AI创作下拉菜单（品牌可经 hiddenSidebarSections 隐藏，如 gxzs 站点） */}
        {!hiddenSidebarSections.includes('creation') && (
        <div style={{ marginBottom: '8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px 12px 15px', // 保持与其他菜单项对齐
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
            }}
            onClick={toggleCreation}
          >
            <span
              style={{
                color: isCreationActive ? activeColor : '#FFF',
                opacity: isCreationActive ? 1 : 0.5,
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: isCreationActive ? '600' : '400',
                lineHeight: '16px',
                marginRight: '4px',
              }}
            >
              {t('sidebar.aiCreation')}
            </span>
            <img
              src={ArrowIcon}
              alt="toggle creation"
              style={{
                width: '13px',
                height: '13px',
                flexShrink: 0,
                opacity: isCreationActive ? 1 : 0.5,
                transform: creationOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.3s, opacity 0.2s ease',
              }}
            />
          </div>

          {creationOpen && (
            <div style={{ padding: '0 8px' }}>
              {/* 创意画布（置于文本创作上方） */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '17px',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={AiAgent}
                    alt={t('sidebar.canvas')}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>
                <div
                  onClick={() => navigate('/canvas-projects')}
                  onMouseEnter={e => {
                    if (!isPathActive('/canvas-projects')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isPathActive('/canvas-projects')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/canvas-projects')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/canvas-projects') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/canvas-projects') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.canvas')}
                  </span>
                </div>
              </div>

        {/* 专家&Skills（紧挨创意画布下方） */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '17px',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <ExpertSkillsNavIcon color={isPathActive('/expert-skills') ? '#FF6D49' : '#FFF'} />
                </div>
                <div
                  onClick={() => {
                    handleMouseLeave();
                    navigate('/expert-skills');
                  }}
                  onMouseEnter={e => {
                    handleMouseEnter('/expert-skills');
                    if (!isPathActive('/expert-skills')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    handleMouseLeave();
                    if (!isPathActive('/expert-skills')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/expert-skills')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px', // 与创意画布子项左内边距对齐
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/expert-skills') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/expert-skills') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.expertSkills')}
                  </span>
                </div>
              </div>
              {/* 文本创作 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                {/* 图标定位在红线上 */}
                <div
                  style={{
                    position: 'absolute',
                    left: '17px', // 调整为32px以补偿父容器的8px padding
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/text') ? ActiveTextIcon : InactiveTextIcon}
                    alt={t('sidebar.textCreation')}
                    style={{
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </div>

                <PreloadLink
                  to="/creation/text"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/text')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px', // 增加左侧内边距给图标空间
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    transition: 'all 0.2s ease',
                    position:'relative'
                  }}
                  onMouseEnter={e => {
                    if (!isPathActive('/creation/text')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isPathActive('/creation/text')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/text') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/text') ? '600' : '400',
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.textCreation')}
                  </span>
                  <span
                    style={{
                      position:'absolute',
                      right:'0',
                      padding: '0px 6px 2px',
                      borderRadius: '6px',
                      border: '1px solid #FF6D49',
                      background: 'rgba(255, 109, 73, 0.3)',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: '#FFF', fontSize: '10px', fontWeight: 400, lineHeight: '14px', whiteSpace: 'nowrap' }}>{t('sidebar.promptReverse')}</span>
                    <span style={{color: '#FFF', background: '#FF6D49', fontSize: '8px', fontWeight: 700, lineHeight: '14px',borderRadius:'2px' ,padding:'0 2px',marginLeft:'4px'}}>NEW</span>
                  </span>
                </PreloadLink>
              </div>

              {/* 图片创作 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                {/* 图标定位在红线上 */}
                <div
                  style={{
                    position: 'absolute',
                    left: '17px', // 调整为32px以补偿父容器的8px padding
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/image') ? ActiveImageIcon : InactiveImageIcon}
                    alt={t('sidebar.imageCreation')}
                    style={{
                      width: '17px',
                      height: '17px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleCreationClick('/creation/image')}
                  onMouseEnter={e => {
                    handleMouseEnter('/creation/image');
                    if (!isPathActive('/creation/image')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    handleMouseLeave();
                    if (!isPathActive('/creation/image')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/image')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px', // 增加左侧内边距给图标空间
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/image') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/image') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.imageCreation')}
                  </span>
                </div>
              </div>

              {/* 视频创作 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                {/* 图标定位在红线上 */}
                <div
                  style={{
                    position: 'absolute',
                    left: '17px', // 调整为32px以补偿父容器的8px padding
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/video') ? ActiveVideoIcon : InactiveVideoIcon}
                    alt={t('sidebar.videoCreation')}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleCreationClick('/creation/video')}
                  onMouseEnter={e => {
                    handleMouseEnter('/creation/video');
                    if (!isPathActive('/creation/video')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    handleMouseLeave();
                    if (!isPathActive('/creation/video')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/video')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px', // 增加左侧内边距给图标空间
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/video') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/video') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.videoCreation')}
                  </span>
                </div>
              </div>

              {/* 视频剪辑（品牌可经 hiddenSidebarItems 隐藏，如 juxflow 站点） */}
              {!hiddenSidebarItems.includes('videoEdit') && (
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                {/* 图标定位在红线上 */}
                <div
                  style={{
                    position: 'absolute',
                    left: '17px', // 调整为32px以补偿父容器的8px padding
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={
                      isPathActive('/creation/edit-video')
                        ? VideoEditActive
                        : VideoEdit
                    }
                    alt={t('sidebar.videoEdit')}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleCreationClick('/creation/edit-video')}
                  onMouseEnter={e => {
                    handleMouseEnter('/creation/edit-video');
                    if (!isPathActive('/creation/edit-video')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    handleMouseLeave();
                    if (!isPathActive('/creation/edit-video')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/edit-video')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px', // 增加左侧内边距给图标空间
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/edit-video') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/edit-video') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.videoEdit')}
                  </span>
                </div>
              </div>
              )}

              {/* 小工具 */}
              <div style={{ position: 'relative', marginBottom: '4px' }}>
                {/* 图标定位在红线上 */}
                <div
                  style={{
                    position: 'absolute',
                    left: '17px', // 调整为32px以补偿父容器的8px padding
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={isPathActive('/creation/tools') ? ActiveToolsIcon : InactiveToolsIcon}
                    alt={t('sidebar.tools')}
                    style={{
                      width: '19px',
                      height: '19px',
                    }}
                  />
                </div>

                <div
                  onClick={() => handleCreationClick('/creation/tools')}
                  onMouseEnter={e => {
                    handleMouseEnter('/creation/tools');
                    if (!isPathActive('/creation/tools')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    handleMouseLeave();
                    if (!isPathActive('/creation/tools')) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '197px',
                    height: '40px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    background: isPathActive('/creation/tools')
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'transparent',
                    padding: '12px 16px 12px 35px', // 增加左侧内边距给图标空间
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: isPathActive('/creation/tools') ? '#FF6D49' : '#FFF',
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: isPathActive('/creation/tools') ? 600 : 400,
                      lineHeight: '16px',
                    }}
                  >
                    {t('sidebar.tools')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* 横向分隔线（AI创作区块隐藏时此线去掉，避免与下一条相邻成双线） */}
        {!hiddenSidebarSections.includes('creation') && (
          <div style={{ height: '1px', backgroundColor: borderColor, margin: '16px 0' }} />
        )}

        {/* 画布 — 已移至 AmeTV 位置，此处隐藏 */}
        {/* <div style={{ marginBottom: '8px', position: 'relative' }}>
          ...
        </div> */}


        {/* 资产 */}
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          {/* 图标定位在红线上 */}
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={isPathActive('/assets') ? ActiveAssetsIcon : InactiveAssetsIcon}
              alt={t('sidebar.assets')}
              style={{
                width: '17px',
                height: '17px',
              }}
            />
          </div>

          <div
            onClick={() => {
              const navState: RouteState = { showSidebar: true };
              navigate('/assets', { state: navState });
            }}
            onMouseEnter={e => {
              handleMouseEnter('/assets');
              if (!isPathActive('/assets')) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }
            }}
            onMouseLeave={e => {
              handleMouseLeave();
              if (!isPathActive('/assets')) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/assets') 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'transparent',
              padding: '12px 16px 12px 44px', // 增加左侧内边距给图标空间
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/assets') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/assets') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.assets')}
            </span>
          </div>
        </div>

        {/* 任务 */}
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          {/* 图标定位在红线上 */}
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 21 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.5 5.00293C3.50003 4.59066 3.97125 4.35576 4.30078 4.60352L6.95605 6.59961C7.08146 6.69402 7.1552 6.84205 7.15527 6.99902V16.4971C7.15525 16.9093 6.68403 17.1442 6.35449 16.8965L3.69922 14.9004C3.57377 14.806 3.50007 14.658 3.5 14.501V5.00293Z"
                stroke={isPathActive('/tasks') ? '#FF6D49' : '#FFF'}
              />
              <path
                d="M8.67236 16.4971C8.67239 16.9093 9.14361 17.1442 9.47314 16.8965L12.1284 14.9004C12.2538 14.806 12.3276 14.658 12.3276 14.501V5.00293C12.3276 4.59066 11.8564 4.35575 11.5269 4.60352L8.87158 6.59961C8.74614 6.69402 8.67244 6.84202 8.67236 6.99902V16.4971Z"
                stroke={isPathActive('/tasks') ? '#FF6D49' : '#FFF'}
              />
              <path
                d="M17.6553 16.4971C17.6552 16.9093 17.184 17.1442 16.8545 16.8965L14.1992 14.9004C14.0738 14.806 14.0001 14.658 14 14.501V5.00293C14 4.59066 14.4712 4.35575 14.8008 4.60352L17.4561 6.59961C17.5815 6.69402 17.6552 6.84202 17.6553 6.99902V16.4971Z"
                stroke={isPathActive('/tasks') ? '#FF6D49' : '#FFF'}
              />
            </svg>
          </div>

          <div
            onClick={() => {
              const navState: RouteState = { showSidebar: true };
              navigate('/tasks', { state: navState });
            }}
            onMouseEnter={e => {
              handleMouseEnter('/tasks');
              if (!isPathActive('/tasks')) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }
            }}
            onMouseLeave={e => {
              handleMouseLeave();
              if (!isPathActive('/tasks')) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/tasks')
                ? 'rgba(255, 255, 255, 0.05)'
                : 'transparent',
              padding: '12px 16px 12px 44px', // 增加左侧内边距给图标空间
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/tasks') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/tasks') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.taskHall')}
            </span>
          </div>
        </div>
              
        {/* 横向分隔线 */}
        {!hiddenSidebarSections.includes('ricegrain-divider') && (
        <div style={{ height: '1px', backgroundColor: borderColor, margin: '16px 0' }} />
        )}
        {/* 米粒值 */}
       {!hiddenSidebarSections.includes('ricegrain') && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          {/* 图标定位在红线上 */}
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={AssetsIconRicegrainvalue}
              alt={t('sidebar.ricegrain')}
              style={{
                width: '17px',
                height: '17px',
              }}
            />
          </div>

          <div
            onClick={() => {
              const navState: RouteState = { showSidebar: true };
              navigate('/ricegrain/purchase', { state: navState });
            }}
            onMouseEnter={e => {
              handleMouseEnter('/ricegrain/purchase');
              if (!isPathActive('/ricegrain/purchase')) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }
            }}
            onMouseLeave={e => {
              handleMouseLeave();
              if (!isPathActive('/ricegrain/purchase')) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/ricegrain/purchase')
                ? 'rgba(255, 255, 255, 0.05)'
                : 'transparent',
              padding: '12px 16px 12px 44px', // 增加左侧内边距给图标空间
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/ricegrain/purchase') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/ricegrain/purchase') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.ricegrain')}
            </span>
          </div>
        </div>
       )}

        {/* 会员 */}
       {!hiddenSidebarSections.includes('membership') && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: '24px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={MemberIcon}
              alt={t('sidebar.member')}
              style={{
                width: '17px',
                height: '17px',
              }}
            />
          </div>

           <div
            onClick={() => {
              const navState: RouteState = { showSidebar: true };
              navigate('/ricegrain/membership', { state: navState });
            }}
            onMouseEnter={() => handleMouseEnter('/ricegrain/membership')}
            onMouseLeave={handleMouseLeave}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '197px',
              height: '40px',
              flexShrink: 0,
              borderRadius: '10px',
              background: isPathActive('/ricegrain/membership')
                ? 'rgba(255, 255, 255, 0.05)'
                : 'transparent',
              padding: '12px 16px 12px 44px',
              boxSizing: 'border-box',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                color: isPathActive('/ricegrain/membership') ? '#FF6D49' : '#FFF',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: isPathActive('/ricegrain/membership') ? 600 : 400,
                lineHeight: '16px',
              }}
            >
              {t('sidebar.member')}
            </span>
          </div>
        </div>
       )}

        {/* 横向分隔线 */}
        <div style={{ height: '1px', backgroundColor: borderColor, margin: '16px 0' }} />

        {!hiddenSidebarItems.includes('invite') && !isTeamSpace && (
        <div style={{ marginBottom: '8px', position: 'relative' }}>
          {userInfo ? (
            // 已登录且选中：橙色背景按钮，居中
            <div
              onClick={() => { const navState: RouteState = { showSidebar: true }; navigate('/invite', { state: navState }); }}
              onMouseEnter={() => handleMouseEnter('/invite')}
              onMouseLeave={handleMouseLeave}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '197px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(90deg, #FF6D49 0%, #FF8A6C 100%)',
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(255, 109, 73, 0.2)',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #FF5A36 0%, #FF7759 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 109, 73, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #FF6D49 0%, #FF8A6C 100%)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 109, 73, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <img
                src={InviteIcon}
                alt={t('sidebar.inviteFriends')}
                style={{ width: '18px', height: '18px', filter: 'brightness(0) invert(1)', flexShrink: 0 }}
              />
              <span
                style={{
                  color: '#FFFFFF',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontSize: '15px',
                  fontStyle: 'normal',
                  lineHeight: '18px',
                  letterSpacing: '0.5px',
                }}
              >
                {t('sidebar.inviteFriends')}
              </span>
            </div>
          ) : (
            // 未登录 或 未选中：无背景，橙色图标+橙色文字，左对齐
            <div
              onClick={() => { const navState: RouteState = { showSidebar: true }; navigate('/invite', { state: navState }); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '197px',
                height: '40px',
                padding: '0 16px',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <img
                src={InviteIcon}
                alt={t('sidebar.inviteFriends')}
                style={{
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                  filter: 'brightness(0) saturate(100%) invert(55%) sepia(80%) saturate(600%) hue-rotate(330deg) brightness(105%)',
                }}
              />
              <span
                style={{
                  color: '#FF6D49',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '16px',
                }}
              >
                {t('sidebar.inviteFriends')}
              </span>
            </div>
          )}
        </div>
        )}
      </div>

      {/* ICP 备案信息（品牌可配，如 gxzs 站点）：底部用户信息上方展示，跳工信部备案系统 */}
      {icpRecord && (
        <a
          href={icpRecord.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            flexShrink: 0,
            padding: '8px 0 16px',
            background: '#101010',
            color: 'rgba(255, 255, 255, 0.35)',
            fontSize: '11px',
            lineHeight: '16px',
            textAlign: 'center',
            textDecoration: 'none',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.35)'; }}
        >
          {icpRecord.text}
        </a>
      )}

      {/* 底部边框线：置于备案标签与底部用户信息之间（未配置备案时紧贴用户信息上方，与原布局一致） */}
      <div
        style={{
          height: '1px',
          background: borderColor,
          margin: '0 16px',
        }}
      ></div>

      {/* 底部用户信息 - 使用 flexbox 固定在底部（V0.1 团队协作：AccountMenu 两行账户卡 + 点击浮层） */}
      <AccountMenu />
    </div>
  );
};

export default Sidebar;
